// Display-only helpers shared by every media presentation. Nothing here
// rewrites stored filenames, captions, removal confirmation or download identity.

// Known mojibake for space-like characters: UTF-8 bytes of U+202F / U+00A0 /
// U+2009 read as Windows-1252 or Latin-1. Deliberately narrow so valid
// non-ASCII names (accents, CJK) are never reinterpreted.
const MOJIBAKE = [
  ["\u00e2\u20ac\u00af", " "], // U+202F via cp1252
  ["\u00e2\u0080\u00af", " "], // U+202F via latin-1
  ["\u00e2\u00af", " "],       // U+202F with the C1 byte dropped ("â¯")
  ["\u00e2\u20ac\u2030", " "], // U+2009 via cp1252
  ["\u00c2\u00a0", " "],       // U+00A0 via cp1252/latin-1
];
const SPACE_LIKE = /[\u00a0\u2007\u2009\u202f]/g;

export function normalizeDisplayName(value) {
  if (typeof value !== "string") return "";
  let out = value;
  for (const [bad, good] of MOJIBAKE) out = out.split(bad).join(good);
  try { out = out.normalize("NFC"); } catch { /* old engines: leave as-is */ }
  return out.replace(SPACE_LIKE, " ").trim();
}

export function splitExtension(name) {
  const m = /^(.*\S)\.([A-Za-z0-9]{1,5})$/.exec(name);
  return m ? { base: m[1], ext: m[2].toLowerCase() } : { base: name, ext: "" };
}

// One primary label only; stored download identity is never rewritten.
export function mediaLabels(item) {
  const rawFilename = typeof item?.filename === "string" ? item.filename : "";
  const rawCaption = typeof item?.caption === "string" ? item.caption : "";
  const filename = normalizeDisplayName(rawFilename);
  // Stored captions are shown exactly; only the default caption (equal to the
  // original filename) gets display-filename normalization.
  const caption = rawCaption === rawFilename ? "" : rawCaption;
  if (caption.trim()) return { primary: caption, secondary: null, filename };
  const fallback = filename || normalizeDisplayName(rawCaption);
  const { base } = splitExtension(fallback);
  return { primary: base || "Untitled file", secondary: null, filename };
}

const isImage = item => item?.type === "image";
const time = item => { const t = Date.parse(item?.createdAt || ""); return Number.isFinite(t) ? t : Number.MAX_SAFE_INTEGER; };
export function orderMedia(items = [], coverId = null) {
  return [...items].sort((a,b) => {
    if (String(a.id) === String(coverId)) return -1;
    if (String(b.id) === String(coverId)) return 1;
    return (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
      time(a)-time(b) || String(a.id).localeCompare(String(b.id));
  });
}

// Explicit cover = coverMediaId names a listed image. Otherwise the server's
// effectiveCover (automatic first-image fallback) or the first ordered image.
// "explicit" drives the Set/Clear control semantics.
export function resolveCover({ items = [], coverMediaId = null, effectiveCover = null } = {}) {
  const images = items.filter(isImage);
  const find = id => id == null ? null : images.find(item => String(item.id) === String(id)) || null;
  const explicit = find(coverMediaId);
  if (explicit) return { id: explicit.id, explicit: true };
  if (effectiveCover?.legacy) return { id: null, explicit: false };
  const eff = find(effectiveCover?.id ?? effectiveCover?.mediaId);
  if (eff) return { id: eff.id, explicit: false };
  const first = orderMedia(images)[0];
  return first ? { id: first.id, explicit: false } : { id: null, explicit: false };
}

// Cover first, then further unique items in list order, at most `max`.
export function previewSelection(items = [], coverId = null, max = 4) {
  const seen = new Set();
  const out = [];
  const cover = coverId == null ? null : items.find(item => String(item.id) === String(coverId));
  for (const item of cover ? [cover, ...orderMedia(items)] : orderMedia(items)) {
    const key = String(item.id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
    if (out.length >= max) break;
  }
  const unique = new Set(items.map(i => String(i.id))).size;
  return { shown: out, overflow: Math.max(0, unique - out.length), total: unique };
}

export const DRAWER_UPLOAD_TYPES = /^(image\/(png|jpeg|webp)|application\/pdf)$/;
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
export function uploadRejection(file, imagesOnly = false) {
  const supported = imagesOnly ? /^image\/(png|jpeg|webp)$/.test(file.type) : DRAWER_UPLOAD_TYPES.test(file.type);
  if (!supported) return imagesOnly ? "Choose PNG, JPEG or WebP images (PDF is also supported by the picker)." : "Choose PNG, JPEG, WebP or PDF files.";
  if (file.size > MAX_UPLOAD_BYTES || file.size === 0) return "File must be nonempty and at most 20 MB.";
  return null;
}

// Caption text for any reader: stored captions exactly; the default caption
// (identical to the original filename) shown as the display filename.
export function displayCaption(item) {
  const caption = typeof item?.caption === "string" ? item.caption : "";
  const filename = typeof item?.filename === "string" ? item.filename : "";
  if (filename && caption === filename) return normalizeDisplayName(filename);
  return caption;
}
